import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AccountType, IExternalUserListItem, IProviderSearchResult } from '@core/model';
import { AccountTypeInfo } from '@core/model/accountTypeInfo';
import { UsersService } from '@core/services';
import { TableColumn } from '@mynexus/mynexus-ui-lib';
import { UtilitiesService } from '@core/services/utilities.service';
import { cloneDeep, isEmpty } from 'lodash-es';
import { MessageService, SelectItem } from 'primeng/api';
import { Table, TableLazyLoadEvent } from 'primeng/table';
import { finalize, map, timeout } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TenantsService } from '@core/services';
import { WccAccountTypes } from '@core/enums/wcc-account-type.enum';
import { ChappAccountTypes } from '@core/enums/chapp-account-type.enum';
import { TenantNames } from '@mynexus/mynexus-ui-lib';
import { getAccountTypeOptions } from '@utils/account-type-utilities';
import { AccountTypes, RestrictedUserEmails, RestrictedUserNames } from '@core/constants';

interface HealthPlanSelectItemValue {
  tenantId: string;
  healthPlanName: string;
}

@Component({
  selector: 'app-external-users-list',
  templateUrl: './external-users-list.component.html',
  styleUrls: ['./external-users-list.component.scss'],
  standalone: false
})
export class ExternalUsersListComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private loaderTimeout: ReturnType<typeof setTimeout> | undefined;

  @ViewChild('dt', { static: true }) dataTable!: Table;

  rowsPerPage: number = 10;
  readonly defaultSortField = 'createdOn';
  readonly defaultSortOrder = 1;
  readonly defaultSortOrderOnLoad = -1;

  readonly defaultFilters: TableLazyLoadEvent = {
    first: 0,
    rows: this.rowsPerPage,
    sortField: this.defaultSortField,
    sortOrder: this.defaultSortOrderOnLoad,
    filters: {}
  };

  private readonly restrictedEmails = new Set(RestrictedUserEmails);
  private readonly restrictedNames = new Set(RestrictedUserNames);

  cols: TableColumn[];
  isLoading = true;
  totalRecords = 0;
  users: IExternalUserListItem[] = [];
  email: string[] = [];
  displayName: string[] = [];
  filtersData: string[] = [];
  healthPlans: string[] = [];
  accountType: string[] = [];
  active!: boolean | null;
  createdOnRange: Date[] | null = null;
  tableFilters!: TableLazyLoadEvent;
  @ViewChild('calendarRef') calendarRef: any;
  @ViewChild('accountTypeDropdown') accountTypeDropdown: any;
  @ViewChild('healthPlansDropdown') healthPlansDropdown: any;

  accountTypes?: AccountTypeInfo = {};
  informationWidgethealthPlans: SelectItem<HealthPlanSelectItemValue>[] = [];
  informationWidgetaccountTypes: SelectItem[] = [];
  informationWidgetapplicableAccountTypes: SelectItem[] = [];
  filteredHealthPlansList: { label: string; value: string }[] = [];
  filteredAccountTypesList: { label: string; value: string }[] = [];
  activesList: SelectItem[] = [
    { value: true, label: 'Active' },
    { value: false, label: 'Inactive' }
  ];
  informationWidgethealthPlan = '';

  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private usersService: UsersService,
    private router: Router,
    private messageService: MessageService,
    private utilitiesService: UtilitiesService,
    private tenantsService: TenantsService
  ) {
    this.cols = [
      new TableColumn('', ''),
      new TableColumn('Business Email', 'email', '14rem'),
      new TableColumn('Display Name', 'displayName'),
      new TableColumn('Health Plan', 'healthPlans'),
      new TableColumn('Account Type', 'accountType'),
      new TableColumn('Active', 'isApproved'),
      new TableColumn('Created On', 'createdOn')
    ];
  }

  ngOnInit(): void {
    this.getAccountTypeInfo();
    this.initializeFilters();
  }

  initializeFilters(): void {
    forkJoin({
      accountTypes: this.utilitiesService.accountTypes,
      tenants: this.tenantsService.getTenantsForRegistration()
    }).subscribe(({ accountTypes, tenants }) => {
      this.informationWidgethealthPlans = [];
      this.informationWidgetaccountTypes = [];

      if (tenants && accountTypes) {
        this.informationWidgethealthPlans = tenants
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((el: { name: any; tenantId: any }) => ({
            label: el.name,
            value: {
              healthPlanName: el.name,
              tenantId: el.tenantId
            }
          }));

        this.informationWidgetaccountTypes = getAccountTypeOptions(accountTypes);

        this.informationWidgetapplicableAccountTypes = this.informationWidgetaccountTypes
          .filter(
            at =>
              !this.informationWidgethealthPlan ||
              this.healthPlanSupportsAccountType(this.informationWidgethealthPlan, at.value)
          )
          .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''));
      }

      this.filteredHealthPlansList = this.informationWidgethealthPlans.map(item => ({
        label: String(item.label),
        value: String(item.value.healthPlanName)
      }));

      this.filteredAccountTypesList = this.informationWidgetapplicableAccountTypes
        .filter(item => item.value !== AccountTypes.CHAPPProvider)
        .map(item => ({
          label: String(item.label),
          value: String(item.value)
        }));
    });
  }

  fetchSuggestions(value: string, prop: string): void {
    const userInput = (value ?? '').toLocaleLowerCase();
    const query = `searchField=${prop}&keyword=${userInput}&isExternal=true`;
    this.usersService
      .getUsersRelevantData(query)
      .pipe(
        timeout(30000),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
      next: (items: string[]) => {
        let filteredResponse = items;

        // Filter out restricted users
        if (prop === "email")
          filteredResponse = items.filter(email => !this.restrictedEmails.has(email));
        else if (prop === "displayName")
          filteredResponse = items.filter(name => !this.restrictedNames.has(name));

        this.filtersData = filteredResponse;
      },
      error: (err) => {
        if (err.name === 'TimeoutError') {
          this.messageService.add({
            severity: 'error',
            detail: 'Request timed out. Please try again later.',
            life: 5000
          });
        } else {
          this.messageService.add({
            severity: 'error',
            detail: err.message || 'An error occurred.',
            life: 5000
          });
        }
      }
    });
  }

  onAutocompleteSelect($event: any, dataKey: string): void {
    const filterValue = (typeof $event.value === 'object' && $event.value !== null)
      ? $event.value[dataKey]
      : $event.value;

    this.dataTable.filter(filterValue, dataKey, 'in');
  }

  onClearAutocomplete(column: string): void {
    this.dataTable.filter(null, column, 'contains');
    this.filtersData = [];
  }

  resetFilters(): void {
    this.dataTable.filters = {};
    this.tableFilters = this.defaultFilters;
    this.dataTable.clear();
    this.dataTable.sort({ field: this.defaultSortField });
    this.dataTable.first = 0;
    this.email = [];
    this.displayName = [];
    this.healthPlans = [];
    this.accountType = [];
    this.active = null;
    this.createdOnRange = null;
  }

  private healthPlanSupportsAccountType(tenantName?: string, accountType?: string): boolean {
    if (this.checkAccountTypeVisibility(accountType)) return false;

    return !this.checkAccountTypeRestriction(tenantName, accountType);
  }

  private checkAccountTypeVisibility(accountType?: string): boolean {
    const hiddenPlansList = [WccAccountTypes.WccProviderAdmin, WccAccountTypes.WccClinician];

    const isHiddenPlan = (type?: string) => {
      return (
        hiddenPlansList.findIndex(
          t => type?.localeCompare(t, undefined, { sensitivity: 'base' }) === 0
        ) !== -1
      );
    };

    return isHiddenPlan(accountType);
  }

  private checkAccountTypeRestriction(tenantName?: string, accountType?: string): boolean {
    const restrictedSupportAccountPlans = [
      ChappAccountTypes.ChappProvider,
      ChappAccountTypes.SpecialtyProgramClinician,
      ChappAccountTypes.SpecialtyProgramProviderAdmin
    ];

    const excludedTenants = [TenantNames.Anthem, TenantNames.Regence, TenantNames.Wellpoint];

    const isRestrictedSupportType = (type?: string) => {
      return (
        restrictedSupportAccountPlans.findIndex(
          t => type?.localeCompare(t, undefined, { sensitivity: 'base' }) === 0
        ) !== -1
      );
    };

    const isExcludedTenant = (name?: string) => {
      return (
        excludedTenants.findIndex(
          t => name?.localeCompare(t, undefined, { sensitivity: 'base' }) === 0
        ) !== -1
      );
    };

    return isRestrictedSupportType(accountType) && !isExcludedTenant(tenantName);
  }

  // tslint:disable-next-line: no-any
  filterTable(value: any, criteria: string, matchMode: string): void {
    this.dataTable.filter(value, criteria, matchMode);
    if (this.accountTypeDropdown?.hide) this.accountTypeDropdown.hide();
    if (this.healthPlansDropdown?.hide) this.healthPlansDropdown.hide();
  }

  onRowExpand(event: any): void {
    this.dataTable.expandedRowKeys = { [event.data.email]: true };
  }

  onUserStatusChanged(): void {
    // Re-trigger lazy load with current filters and sort
    const metadata = this.dataTable.createLazyLoadMetadata();
    this.loadData(metadata);
  }

  validateSelectedDates(value: any, criteria: string, matchMode: string) {
    const filter = this.calendarRef?.el?.nativeElement;
    const filterDates = value;
    const isValidFilterDates = filterDates && Array.isArray(filterDates);

    if (isValidFilterDates && filterDates[1] === null) {
      filter.classList.add('ng-invalid', 'ng-dirty');
      this.messageService.add({
        severity: 'error',
        summary: 'Unable to Filter by Exact Date',
        detail: 'Select the start and end date to filter by a date range.',
        life: 5000
      });
    } else {
      filter.classList.remove('ng-invalid', 'ng-dirty');
      this.filterTable(value, criteria, matchMode);
    }
  }

  loadData(filters: any): void {
    if (!filters.sortField) {
      filters.sortField = this.defaultSortField;
      filters.sortOrder = this.defaultSortOrderOnLoad;
    }

    this.dataTable.sortField = filters.sortField as string;
    // this copy is made because of modifying date range for query
    const copyOfFilters = cloneDeep(filters);

    if (copyOfFilters.sortField === 'healthPlans') {
      copyOfFilters.sortField = 'healthPlans/$count';
    } else {
      copyOfFilters.sortField = filters.sortField;
    }

    this.tableFilters = filters;
    this.checkDateRange(copyOfFilters.filters);

    const filtersData = copyOfFilters.filters ?? {};
    const queryParams: Record<any, any> = {
      take: copyOfFilters.rows,
      skip: copyOfFilters.first,
      isExternal: true,
      orderByDescending: !(filters.sortOrder && filters.sortOrder > 0),
      orderBy: filters.sortField
    };

    Object.keys(filtersData).forEach(propertyName => {
      const filterField = filtersData[propertyName];

      if (!filterField) return;

      // Date field sends data as an array for start/end time.
      if (propertyName === 'createdOn') {
        queryParams.createdDateStartDate = filterField?.value[0];
        queryParams.createdDateEndDate = filterField?.value[1];
      } else {
        const actualValue = Array.isArray(filterField?.value)
          ? filterField.value
          : [filterField.value];
        queryParams[propertyName] = actualValue.join(',');
      }
    });

    const queryString = new URLSearchParams(queryParams).toString();
    this.getUsers(queryString);
    this.initializeFilters();
  }

  getUserAccountTypeToDisplay(user: IExternalUserListItem): string {
    if (!user?.accountType) {
      return '';
    } else if (!this.accountTypes || !this.accountTypes[user.accountType]) {
      return user.accountType;
    } else {
      return (
        this.accountTypes[user.accountType].displayName ??
        this.accountTypes[user.accountType].businessType
      );
    }
  }

  getUsers(queryParams: string): void {
    this.isLoading = true;

    this.loaderTimeout = setTimeout(() => {
      this.isLoading = false;
    }, 30000);

    this.usersService
      .getExternalUsers(queryParams)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
        map(res => {
          res.items = res.items.map(item => {
            item.accountTypeDisplayName = this.getUserAccountTypeToDisplay(item);
            return item;
          });
          return res;
        })
      )
      .subscribe({
        next: (res: IProviderSearchResult<IExternalUserListItem>) => {
          if (res) {
            clearTimeout(this.loaderTimeout);
            this.users = res.items;
            this.totalRecords = res.count;
            this.dataTable.expandedRowKeys = {};
          }
        },
        error: error => {
          clearTimeout(this.loaderTimeout);
          this.messageService.add({
            severity: 'error',
            detail: error.message,
            life: 5000
          });
        }
      });
  }

  editUser(userId: string): void {
    this.router.navigate(['users', userId, 'details']);
  }

  private isFilterEmpty(): boolean {
    return isEmpty(this.tableFilters.filters);
  }

  isClearAllEnabled(): boolean {
    const isSortApplied = !(
      this.dataTable.sortOrder !== this.defaultSortOrderOnLoad ||
      this.dataTable.sortField !== this.defaultSortField
    );

    return isSortApplied && this.isFilterEmpty();
  }

  checkDateRange(filters: any): void {
    Object.keys(filters).forEach(key => {
      if (key === 'createdOn') {
        const filterValue = filters[key].value;

        // Local start of day
        const localStart = new Date(filterValue[0]);
        localStart.setHours(0, 0, 0, 0);
        filterValue[0] = localStart.toISOString(); // UTC start of local day

        // Local end of day
        let localEnd: Date;
        if (filterValue[0] && !filterValue[1]) {
          localEnd = new Date(filterValue[0]);
        } else {
          localEnd = new Date(filterValue[1]);
        }
        localEnd.setHours(23, 59, 59, 999);
        filterValue[1] = localEnd.toISOString(); // UTC end of local day
      }
    });
  }

  private getAccountTypeNames(accountTypes: AccountType[]): AccountTypeInfo {
    const result: AccountTypeInfo = {};

    accountTypes.forEach(bt => (result[bt.businessType] = bt));

    return result;
  }

  private getAccountTypeInfo() {
    this.utilitiesService.accountTypes
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => (this.accountTypes = this.getAccountTypeNames(res)));
  }
}
